import React from 'react';
import { useTranslation } from 'react-i18next';
import { LessonActionBtn } from '../../../components/v2/action-buttons';
import { quizClueIcon } from '../../../assets/images';
import BotMessage from '../../../components/v2/bot-message';
import { useUserStore } from '../../../utils/userStore';

interface InteractionState {
  isAnswered?: boolean;
  isCorrect?: boolean;
  isRewarded?: boolean;
  isExplanationAsked?: boolean;
  isClueAsked?: boolean;
  isTitleDone?: boolean;
  isContentDone?: boolean;
  isChatDone?: boolean;
  wrongOptions?: string[];
}

interface StepActionsProps {
  item: any;
  isCurrent: boolean;
  isAgentLoading: boolean;
  interactionState: InteractionState;
  chatThreadLength: number;
  isVideoEligible: boolean;
  onNext: (isEligible?: boolean) => void;
  onExplanation: () => void;
  onClue: () => void;
  gender: number | undefined;
}

export const StepActions: React.FC<StepActionsProps> = ({
  item,
  isCurrent,
  isAgentLoading,
  interactionState,
  chatThreadLength,
  isVideoEligible,
  onNext,
  onExplanation,
  onClue,
  gender
}) => {
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const firstName = userInfo?.first_name || '';

  const [correctTextDone, setCorrectTextDone] = React.useState(false);
  const [wrongTextDone, setWrongTextDone] = React.useState(false);
  const [clarificationTextDone, setClarificationTextDone] = React.useState(false);
  const [staticTextDone, setStaticTextDone] = React.useState(false);
  const [explanationTextDone, setExplanationTextDone] = React.useState(false);

  const {
    isExplanationAsked,
    isClueAsked,
    isAnswered,
    isCorrect,
    isContentDone,
    isChatDone,
    wrongOptions
  } = interactionState;

  const hasAskedHelp = !!isExplanationAsked || !!isClueAsked;

  React.useEffect(() => {
    setCorrectTextDone(false);
    setWrongTextDone(false);
    setClarificationTextDone(false);
    setStaticTextDone(false);
    setExplanationTextDone(false);
  }, [item.id, isCorrect, hasAskedHelp]);

  // "Busy" = agent is loading OR a chat response is still typing
  const isBusy = isAgentLoading || isChatDone === false;

  // Only show action buttons for the current interaction
  if (!isCurrent) return null;

  // Don't show controls while agent is thinking on a fresh interaction
  if (isAgentLoading && isCurrent && chatThreadLength === 0) return null;

  // 1. VIDEO Step Logic
  if (item.type === 'video') {
    if (chatThreadLength === 0) return null;
    if (interactionState.isRewarded) return null;

    // Hide buttons while assistant is thinking OR its response is still typing
    if (isBusy) return null;

    return (
      <div className="v2-play-lesson-actions fade-in">
        <LessonActionBtn
          onClick={() => onNext(true)}
          text={t('watchedAndUnderstood')}
          color="black"
          disabled={!isCurrent}
        />
      </div>
    );
  }

  // 2. QUIZ Step Logic
  if (item.type === 'quiz') {
    if (!isContentDone) return null;
    if (isBusy) return null;

    const hasWrongAttempt = wrongOptions && wrongOptions.length > 0;

    return (
      <>
        {isCorrect && chatThreadLength === 0 && (
          <div className="v2-play-lesson-msg-wrapper fade-in">
            <BotMessage
              text={t(gender === 2 ? 'quizCorrectInteraction_female' : 'quizCorrectInteraction_male', { firstName })}
              variant="correct"
              skipAnimation={!isCurrent}
              playAudio={false}
              id={`quiz-correct-text-${item.id}`}
              onComplete={() => setCorrectTextDone(true)}
            />
          </div>
        )}

        {hasWrongAttempt && !isCorrect && !hasAskedHelp && (
          <div className="v2-play-lesson-msg-wrapper fade-in">
            <BotMessage
              text={t('quizWrongInteraction')}
              variant="correct"
              skipAnimation={!isCurrent}
              playAudio={false}
              id={`quiz-wrong-text-${item.id}`}
              onComplete={() => setWrongTextDone(true)}
            />
          </div>
        )}

        {hasWrongAttempt && !isCorrect && hasAskedHelp && (
          <div className="v2-play-lesson-msg-wrapper fade-in">
            <BotMessage
              text={t(gender === 2 ? 'quizWrongExplanationClarification_female' : 'quizWrongExplanationClarification_male', { firstName })}
              variant="correct"
              skipAnimation={!isCurrent}
              playAudio={false}
              id={`quiz-wrong-clarification-${item.id}`}
              onComplete={() => setClarificationTextDone(true)}
            />
          </div>
        )}

        <div className="v2-play-lesson-actions v2-play-lesson-actions--quiz fade-in">
          <div className="v2-play-lesson-actions__row">
            {!hasWrongAttempt && !isCorrect && (
              <>
                {!isClueAsked && (
                  <LessonActionBtn
                    onClick={onClue}
                    text={t('clue')}
                    color="black"
                    icon={<img src={quizClueIcon} alt="clue" />}
                    disabled={!isCurrent || isAgentLoading}
                  />
                )}
                <LessonActionBtn
                  onClick={() => onNext()}
                  text={t('lessonContinue')}
                  color="black"
                  disabled={!isCurrent || isAgentLoading}
                />
              </>
            )}

            {isCorrect && (correctTextDone || chatThreadLength > 0) && (
              <LessonActionBtn
                onClick={() => onNext()}
                text={t('lessonContinue')}
                color="black"
                disabled={!isCurrent || isAgentLoading}
              />
            )}

            {hasWrongAttempt && !isCorrect && (
              <>
                {!hasAskedHelp && wrongTextDone && (
                  <LessonActionBtn
                    onClick={onExplanation}
                    text={t('explain')}
                    color="black"
                    disabled={!isCurrent || isAgentLoading}
                  />
                )}
                {hasAskedHelp && clarificationTextDone && (
                  <LessonActionBtn
                    onClick={() => onNext()}
                    text={t('lessonContinue')}
                    color="black"
                    disabled={!isCurrent || isAgentLoading}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // 3. CONTENT / SUMMARY Step Logic
  if (item.type === 'content' || item.type === 'summary') {
    if (!isContentDone) return null;
    if (isBusy) return null;

    const canShowActions = 
      (item.type === 'summary') ||
      (item.type === 'content' && !isExplanationAsked && (staticTextDone || chatThreadLength > 0)) ||
      (isExplanationAsked && (explanationTextDone || chatThreadLength > 0));

    return (
      <>
        {item.type === 'content' && !isExplanationAsked && chatThreadLength === 0 && (
          <div className="v2-play-lesson-msg-wrapper">
            <BotMessage
              text={t(gender === 2 ? 'v2StepActionsStaticText_female' : 'v2StepActionsStaticText_male')}
              variant="correct"
              skipAnimation={!isCurrent}
              playAudio={false}
              id={`static-text-${item.id}`}
              typingDuration={1500}
              onComplete={() => setStaticTextDone(true)}
            />
          </div>
        )}
        {isExplanationAsked && (
          <div className="v2-play-lesson-msg-wrapper">
            <BotMessage
              text={t(gender === 2 ? 'v2ExplanationClarification_female' : 'v2ExplanationClarification_male')}
              variant="correct"
              skipAnimation={!isCurrent}
              playAudio={false}
              id={`clarification-text-${item.id}`}
              typingDuration={1500}
              onComplete={() => setExplanationTextDone(true)}
            />
          </div>
        )}
        {canShowActions && (
          <div className="v2-play-lesson-actions fade-in">
            <div className="v2-play-lesson-actions__row">
              {!isExplanationAsked && (
                <LessonActionBtn
                  onClick={onExplanation}
                  text={t('wantExplanation')}
                  variant="outlined"
                  disabled={!isCurrent}
                />
              )}
              <LessonActionBtn
                onClick={() => onNext()}
                text={t('lessonContinue')}
                color="black"
                disabled={!isCurrent}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
};
